import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { QrCode, Monitor, FileText, ExternalLink, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Tarjeta grande y muy visible en el dashboard admin
 * con acceso directo a las 3 pantallas de la feria de Granada:
 *   1. Landing pública (/granada)
 *   2. Pantalla del stand con QR (/demo-loop)
 *   3. Dossier imprimible (/dossier)
 *
 * Los links abren en pestaña nueva para no perder la sesión admin.
 */
const FeriaAccessCard = () => {
  const [copied, setCopied] = useState(null);
  const baseUrl = 'https://appguardiansfit.com';

  const items = [
    {
      key: 'landing',
      icon: QrCode,
      title: 'Landing feria',
      desc: 'Lo que ve el visitante al escanear el QR (con tu foto y precios)',
      path: '/granada',
      color: 'from-emerald-500/20 to-emerald-500/5',
      accent: 'text-emerald-400',
      testid: 'feria-btn-landing',
    },
    {
      key: 'demo',
      icon: Monitor,
      title: 'Pantalla stand',
      desc: 'Slides rotando con QR grande. Pulsa F11 para pantalla completa',
      path: '/demo-loop',
      color: 'from-amber-500/20 to-amber-500/5',
      accent: 'text-amber-400',
      testid: 'feria-btn-demo',
    },
    {
      key: 'dossier',
      icon: FileText,
      title: 'Folleto (dossier)',
      desc: '3 páginas A4 imprimibles. Botón "Imprimir/PDF" arriba',
      path: '/dossier',
      color: 'from-sky-500/20 to-sky-500/5',
      accent: 'text-sky-400',
      testid: 'feria-btn-dossier',
    },
  ];

  const copyUrl = async (path, key) => {
    try {
      await navigator.clipboard.writeText(`${baseUrl}${path}`);
      setCopied(key);
      toast.success('URL copiada al portapapeles');
      setTimeout(() => setCopied(null), 2000);
    } catch (_e) {
      toast.error('No se pudo copiar');
    }
  };

  return (
    <Card
      className="bg-gradient-to-br from-emerald-500/10 via-transparent to-transparent border-emerald-500/30 border-2"
      data-testid="feria-access-card"
    >
      <CardContent className="p-6">
        {/* Cabecera */}
        <div className="flex items-start justify-between mb-5 flex-wrap gap-2">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <QrCode className="h-5 w-5 text-emerald-400" />
              <h2 className="text-lg font-bold text-foreground">Feria Granada — acceso rápido</h2>
            </div>
            <p className="text-sm text-muted-foreground">
              Pantalla del stand, folleto imprimible y landing del QR
            </p>
          </div>
          <span className="text-[10px] uppercase tracking-widest text-emerald-400/80 border border-emerald-500/30 rounded-full px-3 py-1 self-start">
            Público
          </span>
        </div>

        {/* Grid de 3 accesos */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <div
                key={it.key}
                className={`relative rounded-lg bg-gradient-to-br ${it.color} border border-white/10 p-4 hover:border-white/25 transition-all group`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`h-5 w-5 ${it.accent}`} strokeWidth={2} />
                  <h3 className="font-semibold text-foreground">{it.title}</h3>
                </div>
                <p className="text-xs text-muted-foreground mb-4 min-h-[36px]">
                  {it.desc}
                </p>

                {/* Botones de acción */}
                <div className="flex items-center gap-2">
                  <a
                    href={it.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={it.testid}
                    className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-md bg-white/10 hover:bg-white/20 text-white text-sm font-medium py-2 px-3 transition ${it.accent}`}
                  >
                    Abrir
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                  <button
                    onClick={() => copyUrl(it.path, it.key)}
                    className="rounded-md bg-white/5 hover:bg-white/15 text-muted-foreground p-2 transition"
                    title="Copiar URL"
                    data-testid={`${it.testid}-copy`}
                  >
                    {copied === it.key ? (
                      <Check className="h-4 w-4 text-emerald-400" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>

                {/* URL debajo, muy pequeña */}
                <p className="mt-2 text-[10px] text-muted-foreground/70 font-mono truncate">
                  {baseUrl}{it.path}
                </p>
              </div>
            );
          })}
        </div>

        {/* Tip final */}
        <div className="mt-5 pt-4 border-t border-white/10 flex items-start gap-2 text-xs text-muted-foreground">
          <span className="text-emerald-400 font-bold">💡</span>
          <p>
            Los 3 QR (de la pantalla y del folleto) llevan a{' '}
            <span className="font-mono text-emerald-400">/granada</span>. Cuando alguien se registra desde ahí,
            queda etiquetado como <span className="font-mono text-emerald-400">feria-granada</span> para
            que puedas medir la conversión.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default FeriaAccessCard;
